import {Injectable } from "@angular/core";
import { collection, doc, docData, Firestore, getDocs, onSnapshot } from "@angular/fire/firestore";
import { Data } from "@angular/router";
import { catchError, filter, from, map, Observable, of, Subscription, switchMap, take, timer, withLatestFrom } from "rxjs";
import { TabRepositoryService } from "../pages/tabs/tab-repository.service";
import { CommonService } from "./common/common.service";
import { FirestoreDataService } from "./firestore-data.service";
import { HomeDataService } from "./home-data.service";
import { UserManagementService } from "./user-management.service";

@Injectable({
  providedIn: "root",
})
export class GameBalanceService {
  public challengeSubscription: Subscription;
  constructor(
    private firestore: Firestore,
    private homeDataService: HomeDataService,
    private userManagementService: UserManagementService,
    private firestoreDataService: FirestoreDataService,
    private tabService: TabRepositoryService,
    private commonService: CommonService
  ) { }

  // this code is for updating User point
  updateCurrentBalanceGame(updateBalance: number) {
    this.commonService.getUid("game-balance-service")
      .pipe(take(1)) // take only the first emitted value
      .subscribe({
        next: (userId) => {
          if (!userId) return;
          const path_id = "SK8mBmn1R6bNDdj1cul8";
          const doc_data = { points: updateBalance };
          const method = "update";

          this.firestoreDataService.getV2CloudFunction("updatefirestoredoc", {
            path_id,
            doc_data,
            method,
          }).subscribe({
            error: (error) => {
              console.error('Error updating current balance:', error); // Log error for debugging
            }
          });
        },
        error: (err) => {
          console.error("Failed to fetch userId in updateCurrentBalanceGame:", err);
        }
      });
  }


  // This code is used to determine whether the user has won a prize. It checks to see if the user is logged in and if they have won a prize, using the user's id to check the user's inbox. If the user is logged in and has won a prize, the code returns true. Otherwise, it returns false.
  isWinning$ = this.homeDataService.homePageLoaded$.pipe(
    withLatestFrom(this.userManagementService.userRequest$),
    switchMap(([user]) => {
      if (!user) {
        return of(false);
      }
      return this.commonService.getUid("isWinning$").pipe(
        switchMap((userId) => {
          if (!userId) {
            return of(false);
          }

          const docRef = doc(
            this.firestore,
            `users/${userId}/in_app_notification/status`
          );

          return docData(docRef).pipe(
            map((notification: Data) => {
              this.tabService.setRingBell(notification?.inbox);
              this.tabService.setRedDotUnread(notification?.inbox);
              return notification?.winning ?? false;
            }),
            catchError((error) => {
              this.errorHandlerLogs('Error in isWinning$', error);
              return of(false);
            })
          );
        })
      );
    })
  );



  /**
   * This function checks if the user has a voucher to claim. If the user has a voucher to claim, it returns true, otherwise it returns false
   */
  isCreditRefresh$ = this.homeDataService.homePageLoaded$.pipe(
    filter((loaded: boolean) => loaded),
    withLatestFrom(this.userManagementService.userRequest$),
    switchMap(([_, user]: any) => {
      if (!user) {
        return of(null);
      }

      return this.commonService.getUid("isCreditRefresh$").pipe(
        switchMap((userId) => {
          if (!userId) {
            return of(null);
          }

          const statusDocRef = doc(
            this.firestore,
            `users/${userId}/in_app_notification/status`
          );

          // Wrap Firestore onSnapshot in an Observable
          return new Observable((observer) => {
            const unsubscribe = onSnapshot(
              statusDocRef,
              (docSnap) => {
                const notification = docSnap.exists() ? docSnap.data() : null;
                observer.next(notification?.credit_refresh ?? null);
              },
              (error) => {
                console.error("[isCreditRefresh$] onSnapshot error:", error);
                observer.next(null);
              }
            );

            // Cleanup subscription
            return () => unsubscribe();
          }).pipe(
            catchError((error) => {
              console.error('Error in isCreditRefresh$:', error);
              return of(null);
            })
          );
        })
      );
    })
  );


  // This function checks if the user has a voucher to claim. If the user has a voucher to claim, it returns true, otherwise it returns false
  isBreakDownCreditRefreshStatus$ = this.commonService.getUid("credit-refresh-status").pipe(
    filter((userId): userId is string => !!userId), // make sure we have a valid userId
    switchMap((userId) =>
      this.firestoreDataService
        .getAngularFireStore(`/users/${userId}/in_app_notification/status`, "snapshotChanges")
        .pipe(
          map((res: Data) => {
            // Defensive check: ensure res is a valid DocumentSnapshot with a data() method
            if (!res || typeof res?.data !== 'function') {
              return null;
            }
            
            const notification = res.data();
            if (!notification || !notification?.credit_refresh) return null;
            return notification.credit_refresh;
          }),
          catchError((error) => {
            this.errorHandlerLogs('Error fetching credit refresh status:', error);
            return of(null);
          })
        )
    ),
    // Add a final catchError to ensure we always return a valid Observable
    catchError((error) => {
      this.errorHandlerLogs('Error in isBreakDownCreditRefreshStatus$ stream:', error);
      return of(null);
    })
  );


  /**
   * This code checks if the user is logged in and the home page is loaded.
   * If both conditions are true, it checks if the user's notification
   * status and returns the status of the "whats_new" property.
   * If there is no notification status, it returns false.
   * If the user is not logged in, it returns false.
   */
  isCreditNotify$ = this.homeDataService.homePageLoaded$.pipe(
    filter((f: boolean) => f), // only when homePageLoaded$ emits true
    withLatestFrom(this.commonService.getUid("isCreditNotify$")),
    switchMap(([_, userId]) => {
      if (!userId) return of(null);

      return this.firestoreDataService
        .getAngularFireStore(`/users/${userId}`, "snapshotChanges")
        .pipe(
          map((res: Data) => {
            const notification = res?.payload?.data() as Data;
            return notification?.notification_seen ?? false;
          }),
          catchError((error) => {
            console.error('Error fetching credit notify status:', error);
            return of(false);
          })
        );
    })
  );


  /**
   * This function checks if the credit refresh notification has been sent (i.e. the user has already seen the notification).
   * If the notification has been sent, we don't want to send it again.
   * The notification has been sent if a credit refresh notification exists in the database and credit_refresh is true.
   */
  isVoucherToClaim$ = this.homeDataService.homePageLoaded$.pipe(
    filter((loaded: boolean) => loaded),
    switchMap(() =>
      this.commonService.getUid("isVoucherToClaim$").pipe(
        switchMap((userId) => {
          if (!userId) return of(false);

          return this.firestoreDataService
            .getAngularFireStore(
              `/users/${userId}/in_app_notification/status`,
              "snapshotChanges"
            )
            .pipe(
              map((res: Data) => {
                const notification = res?.payload?.data() as Data;
                return notification?.voucher ?? false;
              }),
              catchError((error) => {
                console.error('Error fetching voucher status:', error);
                return of(false);
              })
            );
        })
      )
    )
  );


  /**
   * This function is used to check if the user has seen the notification or not.
   */
  isWhatsNew$ = this.homeDataService.homePageLoaded$.pipe(
    filter((loaded: boolean) => loaded),
    switchMap(() =>
      this.commonService.getUid("isWhatsNew$").pipe(
        switchMap((userId) => {
          if (!userId) return of(false);

          return this.firestoreDataService
            .getAngularFireStore(
              `/users/${userId}/in_app_notification/status`,
              "snapshotChanges"
            )
            .pipe(
              map((res: Data) => {
                const notification = res?.payload?.data() as Data;
                return notification?.whats_new?.show_notice ?? false;
              }),
              catchError((error) => {
                console.error('Error fetching whats new status:', error);
                return of(false);
              })
            );
        })
      )
    )
  );


  // This code is used to fetch charity data from the backend
  // It is used in the home page to display charity data
  charityGlobal$ = this.commonService.getUid("charityGlobal$").pipe(
    switchMap(() =>
      this.firestoreDataService
        .getAngularFireStore(`/general_modules/home_card_charity`, "snapshotChanges")
        .pipe(
          map((d: Data) => d?.payload?.data()),
          catchError((error) => {
            console.error('Error fetching charity data:', error);
            return of(null);
          })
        )
    )
  );


  async scheduleDailyChallenge() {
    this.commonService.getUid("scheduleDailyChallenge").pipe(
      take(1),
      switchMap((userId) => {
        if (!userId) return of(null); // guard against null

        return from(
          (async () => {
            try {
              const challengeCollectionRef = collection(this.firestore, `users/${userId}/challenges`);
              const snapshot = await getDocs(challengeCollectionRef);
              let expirySeconds = 0;

              if (!snapshot.empty) {
                const challengesData = snapshot.docs[0].data() as Data;
                expirySeconds = challengesData?.target?.expiry_date?.seconds ?? 0;
              }

              return { userId, expirySeconds };
            } catch (error) {
              console.error('Error fetching challenge data:', error);
              return null;
            }
          })()
        );
      }),
      filter((data): data is { userId: string; expirySeconds: number } => data !== null), // <-- filter nulls safely
      switchMap(({ userId, expirySeconds }) => {
        const now = new Date();
        const scheduleTime = new Date();
        scheduleTime.setHours(0, 0, 0, 0);
        if (now >= scheduleTime) {
          scheduleTime.setDate(now.getDate() + 1);
        }
        const firstCallDelay = scheduleTime.getTime() - now.getTime();
        const msInADay = 24 * 60 * 60 * 1000;

        return timer(firstCallDelay, msInADay).pipe(
          switchMap(() => {
            const currentTime = Math.floor(Date.now() / 1000);
            const timeDiff = expirySeconds - currentTime;

            if (timeDiff <= 0) {
              return of(this.updateIssueGameChallenge(userId));
            } else {
              return of(null);
            }
          }),
          catchError((error) => {
            console.error("Error during daily challenge scheduling:", error);
            return of(null);
          })
        );
      })
    ).subscribe();
  }



  ngOnDestroy() {
    if (this.challengeSubscription) {
      this.challengeSubscription.unsubscribe();
    }
  }


  updateIssueGameChallenge(userId: string) {
    try {
      this.firestoreDataService.getV2CloudFunction("issuegamechallenge", userId, { timeout: 30000 }).subscribe(
        (result) => {
          if (result === null) {
            console.warn('Game challenge update skipped due to AppCheck token issues');
          }
        },
        (error) => {
          console.warn('Non-critical game challenge update failed:', error);
          // Don't throw error for non-critical function
        }
      );
    } catch (error) {
      console.warn("Error in issuegamechallenge, continuing without disruption:", error);
      // Don't throw - this is a non-critical function
    }
  }

  voteCharity(award_id, points) {
    return this.firestoreDataService.requestBodyService.voteCharity(award_id, points).pipe(
      switchMap((payload) => {
        if (!payload) {
          console.warn("voteCharity: no payload built");
          return of(null);
        }

        return this.firestoreDataService.v2CloudFunctionResponse(
          this.firestoreDataService.callableFunction(payload)
        );
      }),
      catchError((err) => {
        console.error("Error in voteCharity:", err);
        return of(null);
      })
    );
  }
  // This method is used to log error messages
  errorHandlerLogs(message: string, error?: Data): { message: string; timestamp: string; error: Data } {
    const log = {
      message,
      timestamp: new Date().toISOString(),
      error: error ? this.formatError(error) : null,
    };
    return log;
  }

  // This method is used to format error messages
  private formatError(error: Data): Data {
    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
    }
    return error;
  }
}
